import React from 'react'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { formSectionTitleCss } from '../../legacy-design-system/text'
import { CsHelpCircledIcon as HelpIcon } from '../../legacy-design-system/icons'
import { theme as deprecatedTheme } from '../../styled'
import {
  IconAdd,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { WithTooltip } from '../../design-system'
import {
  ChildFieldType,
  CustomMetadataFieldInput,
  InputField,
  ParentFieldType
} from './custom-metadata-field-input'
import { CustomMetadataAgentConfig } from './custom-metadata-agent-config'
import { Box, Row } from '../../legacy-design-system/form'
import { GetFormFieldPropsFn } from '../../legacy-design-system/form/form-validator'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Link } from '@contrast-security-inc/design-system-typography'

export type ReactionOperation = 'NOOP' | 'DISABLE'

export interface FormField {
  readonly id?: string
  readonly type?: ParentFieldType
  readonly displayLabel: string
  readonly agentLabel: string
  readonly required: boolean
  readonly unique: boolean
  readonly subfields?: FormSubfield[]
  readonly isTypeEditable?: boolean
}

export interface FormSubfield {
  readonly id?: string
  readonly type?: ChildFieldType
  readonly displayLabel: string
  readonly agentLabel: string
  readonly required: boolean
  readonly unique: boolean
}

export interface FormEvents {
  readonly onRemoveField: (index: number) => void
  readonly onAddField: () => void
  readonly onEdit: (field: InputField) => void
  readonly getFormFieldProps: GetFormFieldPropsFn
}

export interface CustomMetadataFormProps extends FormEvents {
  readonly fields: FormField[]
}

function HelpText({ children }) {
  return (
    <p
      className={css`
        max-width: 260px;
        overflow-wrap: normal;
        white-space: normal;
      `}
    >
      {children}
    </p>
  )
}

export function CustomMetadataForm(props: CustomMetadataFormProps) {
  const { fields, onRemoveField, onAddField, onEdit, getFormFieldProps } = props
  const agentLabels = buildAgentLabels(fields)

  return (
    <div>
      <Row>
        <Box basis={10}>
          <div
            className={css`
              margin-bottom: 10px;
            `}
          >
            <FormattedMessage
              id="title_customMetadata_customFields"
              children={(msg) => (
                <span className={formSectionTitleCss} children={msg} />
              )}
            />
            <div
              className={css`
                display: inline-block;
                vertical-align: text-top;
              `}
            >
              <WithTooltip
                renderTooltipContent={() => (
                  <div>
                    <FormattedMessage
                      id="title_customMetadata_customFields"
                      children={(msg) => (
                        <p
                          className={css`
                            font-weight: ${deprecatedTheme.fontWeights.primary
                              .bold};
                          `}
                        >
                          {msg}
                        </p>
                      )}
                    />
                    <FormattedMessage
                      id="detail_customMetadata_customFields"
                      children={(msg) => <HelpText>{msg}</HelpText>}
                    />
                    <HelpText>
                      <FormattedMessage
                        id={'detail_customMetadata_customFieldType'}
                      />
                    </HelpText>
                  </div>
                )}
                placement="right"
              >
                {({ onMouseEnter, onMouseLeave }) => (
                  <div
                    className={css`
                      margin-left: 5px;
                      margin-top: -3px;
                    `}
                  >
                    <HelpIcon
                      color={theme.color.neutral50}
                      height={16}
                      width={16}
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}
                    />
                  </div>
                )}
              </WithTooltip>
            </div>
          </div>

          <div
            data-testid="custom-metadata-input-rows"
            className={css`
              margin-bottom: 10px;
            `}
          >
            {!!fields &&
              fields.length !== 0 &&
              fields.map((field, index) => {
                const {
                  displayLabel,
                  type,
                  isTypeEditable,
                  required,
                  unique,
                  subfields
                } = field

                const isAgentLabelsDuplicated =
                  fields.filter(
                    (f) =>
                      field.agentLabel !== '' &&
                      f.agentLabel === field.agentLabel
                  ).length > 1

                return (
                  <div
                    key={index}
                    className={css`
                      margin-bottom: 8px;
                      :last-child {
                        margin-bottom: 0px;
                      }
                    `}
                  >
                    <CustomMetadataFieldInput
                      isAgentLabelDuplicated={isAgentLabelsDuplicated}
                      getFormFieldProps={getFormFieldProps}
                      id={index}
                      displayLabel={displayLabel}
                      type={type}
                      isTypeEditable={isTypeEditable}
                      isRequired={required}
                      isUnique={unique}
                      subfields={
                        !!subfields && subfields.length
                          ? subfields.map((subfield) => {
                              return {
                                isRequired: subfield.required,
                                isUnique: subfield.unique,
                                ...subfield
                              }
                            })
                          : undefined
                      }
                      onEdit={onEdit}
                      onRemove={
                        fields.length === 1 && field.isTypeEditable
                          ? undefined
                          : () => onRemoveField(index)
                      }
                    />
                  </div>
                )
              })}
          </div>
          <FormattedMessage
            id={'action_customMetadata_addField'}
            children={(msg) => {
              return (
                <Stack alignItems="center" spacing="0px">
                  <IconAdd size="sm" />
                  <Link onClick={onAddField}>{msg}</Link>
                </Stack>
              )
            }}
          />
        </Box>
      </Row>

      {!!agentLabels && !!agentLabels.length && (
        <Row>
          <Box basis={10}>
            <div
              className={css`
                margin-top: 20px;
              `}
            >
              <CustomMetadataAgentConfig fields={agentLabels} />
            </div>
          </Box>
        </Row>
      )}
    </div>
  )
}

export const buildAgentLabels = (fields: FormField[]): string[] => {
  const agentLabels: string[] = []

  if (!!fields && fields.length) {
    fields.forEach((field) => {
      if (field.agentLabel !== '') {
        if (field.type === 'PERSON_OF_CONTACT') {
          if (!!field.subfields && field.subfields.length) {
            field.subfields.forEach((subfield) => {
              if (subfield.agentLabel !== '') {
                agentLabels.push(subfield.agentLabel)
              }
            })
          }
        } else {
          agentLabels.push(field.agentLabel)
        }
      }
    })
  }

  return agentLabels
}
